import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Crown, Forward, Lock, Pencil, UserRoundCog, Workflow } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type Attachment, type LeaderWork } from '../lib/api';
import { useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime, formatNumber } from '../lib/format';
import { AttachmentList } from './Attachments';
import { TaskHolderName } from './TaskHolder';
import { RUN_LABEL, runTone } from './WorkflowChain';
import { Badge, Button, Card, CardHeader, Detail, statusTone, useToast } from './ui';
import { WORK_LABEL, workTone } from '../pages/LeaderTasks';

/**
 * Leaders Tasks on the task page (operator, 2026-10-07): a Leaders Task is a
 * task between two department leaders, so its page is the task page with these
 * parts — what its leaders may do, where it stands, and the work handed on from
 * it. A task handed on from one shows where it came from and its files.
 */

/** A task's page: a Leaders Task has its own, under Leaders Tasks. */
export function taskPath(task: { id: string; isLeaderTask?: boolean }): string {
  return task.isLeaderTask ? `/leader-tasks/${task.id}` : `/tasks/${task.id}`;
}

/** The move's button, said the way the move reads from where the task is. */
function moveLabel(from: string, to: string): TranslationKey {
  switch (to) {
    case 'IN_PROGRESS':
      return from === 'BLOCKED' ? 'leaderTasks.move.unblock' : from === 'REVIEW' ? 'leaderTasks.move.backToWork' : 'leaderTasks.move.start';
    case 'BLOCKED':
      return 'leaderTasks.move.block';
    case 'REVIEW':
      return from === 'DONE' ? 'leaderTasks.move.reopen' : 'leaderTasks.move.review';
    case 'DONE':
      return 'leaderTasks.move.done';
    case 'CANCELLED':
      return 'leaderTasks.move.cancel';
    default:
      return 'leaderTasks.move.toDo';
  }
}

/** The work handed on is finished and back with this leader, who may mark the Leaders Task done. */
function reviewIsYours(task: any, userId: string | undefined): boolean {
  return (
    task.status === 'REVIEW' &&
    task.leaderWork?.state === 'finished' &&
    task.assigneeUserId === userId &&
    (task.can?.moves ?? []).includes('DONE')
  );
}

/** Moving a Leaders Task — the leader who has it, and the admins; its sender may call it off before it starts. */
function useMove(task: any) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: string) => api.post(`/tasks/${task.id}/status`, { status }),
    onSuccess: (_data, status) => {
      for (const queryKey of [['task', task.id], ['leader-tasks'], ['dashboard']]) void queryClient.invalidateQueries({ queryKey });
      toast.success(t('leaderTasks.moved', { status: enumLabel(status, locale) }));
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/** The page header's buttons for a Leaders Task: edit, hand on, give to another leader, and its moves. */
export function LeaderTaskActions({ task }: { task: any }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const may = task.can ?? {};
  const move = useMove(task);
  // Cancelled is final, so it asks once more, in place — never a popup.
  const [confirming, setConfirming] = useState(false);
  // While finished work waits for its leader, the banner asks them for Done.
  const bannerAsks = reviewIsYours(task, user?.id);
  const moves: string[] = (may.moves ?? []).filter((to: string) => to !== 'CANCELLED' && !(bannerAsks && to === 'DONE'));
  const cancels = (may.moves ?? []).includes('CANCELLED');
  // Done is the review's answer: it leads while the work waits there.
  const primary = task.status === 'REVIEW' ? 'DONE' : null;
  return (
    <>
      {may.edit && (
        <Button icon={<Pencil className="h-4 w-4" />} onClick={() => navigate(`/leader-tasks/${task.id}/edit`)}>
          {t('common.edit')}
        </Button>
      )}
      {may.handOn && (
        <Button variant="primary" icon={<Forward className="h-4 w-4 flip-rtl" />} onClick={() => navigate(`/leader-tasks/${task.id}/hand-on`)}>
          {t('leaderTasks.handOn')}
        </Button>
      )}
      {may.reassign && (
        <Button icon={<UserRoundCog className="h-4 w-4" />} onClick={() => navigate(`/leader-tasks/${task.id}/reassign`)}>
          {t('leaderTasks.reassign')}
        </Button>
      )}
      {moves.map((to) => (
        <Button
          key={to}
          variant={to === primary ? 'primary' : 'secondary'}
          loading={move.isPending && move.variables === to}
          disabled={move.isPending}
          onClick={() => move.mutate(to)}
        >
          {t(moveLabel(task.status, to))}
        </Button>
      ))}
      {cancels &&
        (confirming ? (
          <>
            <Button
              variant="danger"
              loading={move.isPending && move.variables === 'CANCELLED'}
              onClick={() => move.mutate('CANCELLED', { onSettled: () => setConfirming(false) })}
            >
              {t('leaderTasks.move.cancelYes')}
            </Button>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              {t('leaderTasks.move.cancelNo')}
            </Button>
          </>
        ) : (
          <Button variant="ghost" disabled={move.isPending} onClick={() => setConfirming(true)}>
            {t('leaderTasks.move.cancel')}
          </Button>
        ))}
    </>
  );
}

const BANNER_TONES = {
  brand: { box: 'border-primary/30 bg-primary/8', icon: 'text-primary-600 dark:text-primary-400' },
  success: { box: 'border-success/35 bg-success/8', icon: 'text-success-fg dark:text-success' },
  warning: { box: 'border-warning/35 bg-warning/8', icon: 'text-warning-fg dark:text-warning-dark' },
};

/**
 * Where a Leaders Task stands, for the person looking: the leader asked, to do
 * or hand on; the work back for their review; the sender waiting on it; the
 * work called off.
 */
export function LeaderTaskBanner({ task }: { task: any }) {
  const { t, localized } = useI18n();
  const { user } = useAuth();
  const move = useMove(task);
  const work: LeaderWork | null = task.leaderWork ?? null;
  const mine = task.assigneeUserId === user?.id;

  let tone: keyof typeof BANNER_TONES = 'brand';
  let title: string | null = null;
  let body: string | null = null;
  let action: ReactNode = null;

  if (task.status === 'REVIEW' && work?.state === 'finished') {
    tone = 'success';
    if (mine) {
      title = t('leaderTasks.reviewTitle');
      body = t('leaderTasks.reviewBody', { name: localized(task.createdBy) });
      if (reviewIsYours(task, user?.id)) {
        action = (
          <Button variant="primary" loading={move.isPending} onClick={() => move.mutate('DONE')}>
            {t('leaderTasks.move.done')}
          </Button>
        );
      }
    } else {
      title = t('leaderTasks.waitingReview', { name: localized(task.assignee) });
    }
  } else if (work?.state === 'calledOff' && !['DONE', 'CANCELLED'].includes(task.status)) {
    tone = 'warning';
    title = t('leaderTasks.calledOffTitle');
    body = mine ? t('leaderTasks.calledOffBody') : null;
  } else if (mine && task.status === 'TO_DO' && work?.state === 'none') {
    title = t('leaderTasks.yoursTitle', { name: localized(task.createdBy) });
    body = t('leaderTasks.yoursBody');
  }
  if (!title) return null;

  return (
    <div className={`mb-5 flex flex-wrap items-center gap-4 rounded-lg border p-4 ${BANNER_TONES[tone].box}`}>
      <Crown className={`h-6 w-6 shrink-0 ${BANNER_TONES[tone].icon}`} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-title text-[var(--text-primary)]">{title}</p>
        {body && <p className="mt-0.5 text-body-sm text-muted">{body}</p>}
      </div>
      {action}
    </div>
  );
}

/**
 * The work handed on from a Leaders Task: each task, whose it is and how far it
 * has got — a workflow's under its name and level. A task the person may not
 * open (a workflow's step is private) shows whose it is, without a link.
 */
export function HandedOnWorkCard({ task }: { task: any }) {
  const { t, locale, localized } = useI18n();
  const work: LeaderWork | null = task.leaderWork ?? null;
  if (!work) return null;
  // A workflow's tasks together, under the run; a person's task on its own.
  const groups: Array<{ key: string; run: LeaderWork['tasks'][number]['workflowRun']; tasks: LeaderWork['tasks'] }> = [];
  for (const step of work.tasks) {
    const key = step.workflowRunId ?? step.id;
    const group = groups.find((g) => g.key === key);
    if (group) group.tasks.push(step);
    else groups.push({ key, run: step.workflowRun, tasks: [step] });
  }
  return (
    <Card>
      <CardHeader
        title={t('leaderTasks.workTitle')}
        action={work.state !== 'none' ? <Badge tone={workTone(work.state)}>{t(WORK_LABEL[work.state])}</Badge> : undefined}
      />
      {groups.length === 0 ? (
        <p className="text-body-sm text-muted">{t('leaderTasks.workNone')}</p>
      ) : (
        <ul className="space-y-3">
          {groups.map((group) => (
            <li key={group.key} className="rounded-md bg-[var(--surface-sunken)] px-3 py-2.5">
              {group.run && (
                <p className="mb-1.5 flex flex-wrap items-center gap-1.5 text-caption text-muted">
                  <Workflow className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="font-medium text-[var(--text-secondary)]">
                    {group.run.name ? localized({ name: group.run.name, nameAr: group.run.nameAr }) : t('tasks.workflow')}
                  </span>
                  <span className="tabular">
                    · {t('leaderTasks.workflowLevel', {
                      n: formatNumber(group.run.currentLevel, locale),
                      of: formatNumber(group.run.levelCount, locale),
                    })}
                  </span>
                  <Badge tone={runTone(group.run.status)} className="ms-auto">{t(RUN_LABEL[group.run.status])}</Badge>
                </p>
              )}
              <ul className="space-y-1">
                {group.tasks.map((step) => (
                  // The column is narrow: the status drops under the name rather than squeeze it.
                  <li key={step.id} className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 text-body-sm">
                    {step.canOpen ? (
                      <Link to={`/tasks/${step.id}`} className="min-w-0 break-words text-primary-600 dark:text-primary-400 hover:underline">
                        <TaskHolderName task={step} empty={t('common.unassigned')} />
                      </Link>
                    ) : (
                      <span className="flex min-w-0 items-center gap-1.5 text-muted" title={t('workflows.privateStep')}>
                        <Lock className="h-3.5 w-3.5 shrink-0 text-faint" aria-hidden />
                        <span className="break-words">
                          <TaskHolderName task={step} empty={t('common.unassigned')} />
                        </span>
                      </span>
                    )}
                    <Badge tone={statusTone(step.status)}>{enumLabel(step.status, locale)}</Badge>
                    {step.dueAt && (
                      <span className="basis-full text-caption text-faint tabular">
                        {t('tasks.deadline')}: {formatDateTime(step.dueAt, locale)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** A task handed on from a Leaders Task: which one, linked for whoever may open it. */
export function LeaderTaskSourceRow({ task }: { task: any }) {
  const { t } = useI18n();
  if (!task.sourceLeaderTask) return null;
  return (
    <Detail label={t('leaderTasks.fromLeaderTask')}>
      {task.leaderTaskOpens ? (
        <Link to={`/leader-tasks/${task.sourceLeaderTask.id}`} className="text-primary-600 dark:text-primary-400 hover:underline" dir="auto">
          {task.sourceLeaderTask.title}
        </Link>
      ) : (
        <span dir="auto">{task.sourceLeaderTask.title}</span>
      )}
    </Detail>
  );
}

/** The files sent with the Leaders Task a task was handed on from. */
export function LeaderTaskFilesCard({ task }: { task: any }) {
  const { t } = useI18n();
  const files: Attachment[] = task.leaderTaskFiles ?? [];
  if (!task.sourceLeaderTask || files.length === 0) return null;
  return (
    <Card>
      <CardHeader title={t('leaderTasks.filesTitle')} subtitle={t('leaderTasks.filesHint', { title: task.sourceLeaderTask.title })} />
      <AttachmentList attachments={files} showUploader />
    </Card>
  );
}

/** The history lines Leaders Tasks add — or null, for the task page's own labels. */
export function leaderActivityLabel(
  a: any,
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string,
  localized: (row: any) => string,
): string | null {
  switch (a.action) {
    case 'task.handed_on':
      // A saved workflow's name, before workflows were built on the task (2026-10-15).
      return a.metadata?.workflow
        ? a.metadata.workflow.name
          ? t('leaderTasks.activity.handedOnToWorkflow', { workflow: localized(a.metadata.workflow) })
          : t('leaderTasks.activity.handedOnThroughWorkflow')
        : t('leaderTasks.activity.handedOnTo', { name: localized(a.metadata?.assignee) });
    case 'task.work_finished':
      return t('leaderTasks.activity.workFinished');
    case 'task.work_reopened':
      return t('leaderTasks.activity.workReopened');
    case 'task.work_called_off':
      return t('leaderTasks.activity.workCalledOff');
    case 'task.department_changed':
      return t('leaderTasks.activity.departmentChanged', { department: localized(a.metadata?.department) });
    case 'task.created':
      return a.metadata?.fromLeaderTask ? t('leaderTasks.activity.createdFrom', { title: a.metadata.fromLeaderTask }) : null;
    default:
      return null;
  }
}
